import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { TodoDto, todosApi } from 'services/camMirrorService';
import {
  formatModalDate,
  GmDeleteDialog,
  IconCap,
  IconCheck,
  IconClose,
  IconLock,
  IconPencil,
  IconPerson,
  IconPlus,
  IconSearch,
  IconTodo,
  IconTrash,
  IconUsers,
} from './gmModal';

const MAX = 500;

const TodosModal: React.FC<{
  open: boolean;
  onClose: () => void;
  studentId: string | number;
  studentFirstName: string;
  viewerRole: 'counselor' | 'student';
  onActivity?: () => void;
  openScope?: 'counselor' | 'student';
}> = ({ open, onClose, studentId, studentFirstName, viewerRole, onActivity, openScope }) => {
  const viewerFirstName = useAppSelector((store) => store.basicInformation.myBasicInformation?.first_name);
  const [scope, setScope] = useState<'counselor' | 'student'>(openScope ?? (viewerRole === 'student' ? 'student' : 'counselor'));
  useEffect(() => {
    if (open && openScope) setScope(openScope);
  }, [open, openScope]);
  const [items, setItems] = useState<TodoDto[]>([]);
  const [otherCount, setOtherCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [pendingDelete, setPendingDelete] = useState<TodoDto | null>(null);
  // HEADER-DOCS-TODOS-V2: inline edit state for student-authored to-dos.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const name = studentFirstName || 'the student';
  const isStudentViewer = viewerRole === 'student';
  // A to-do written by the student (v1.2). Counselor-assigned to-dos have createdByRole 'counselor'.
  const isStudentAuthored = (todo: TodoDto) => todo.createdByRole === 'student';
  // Students may edit/delete only their own to-dos (the backend enforces the same rule);
  // counselors keep delete on every to-do, as in v1.1.
  const canEditTodo = (todo: TodoDto) => isStudentViewer && isStudentAuthored(todo);
  const canDeleteTodo = (todo: TodoDto) => viewerRole === 'counselor' || canEditTodo(todo);

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(query.trim().toLowerCase()), 150);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const load = viewerRole === 'student' ? todosApi.markRead(studentId).catch(() => undefined) : Promise.resolve();
    load
      .then(() => todosApi.list(studentId, viewerRole === 'counselor' ? scope : 'student'))
      .then((page) => {
        if (!cancelled) setItems(page.items);
        if (!cancelled && viewerRole === 'student') onActivity?.();
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, studentId, scope, viewerRole, onActivity]);

  useEffect(() => {
    if (!open || viewerRole !== 'counselor') return;
    let cancelled = false;
    const other = scope === 'counselor' ? 'student' : 'counselor';
    todosApi
      .list(studentId, other)
      .then((page) => {
        if (!cancelled) setOtherCount(page.items.length);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, studentId, viewerRole, scope, items.length]);

  const visible = useMemo(() => {
    const matched = filter ? items.filter((todo) => todo.description.toLowerCase().includes(filter)) : items;
    return matched;
  }, [items, filter]);

  const addTodo = async () => {
    const description = draft.trim();
    if (!description || saving) return;
    setSaving(true);
    try {
      const created = await todosApi.create(studentId, scope, description);
      setItems((prev) => [created, ...prev]);
      setDraft('');
      onActivity?.();
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (todo: TodoDto) => {
    const next = !todo.isCompleted;
    setItems((prev) => prev.map((item) => (item.externalId === todo.externalId ? { ...item, isCompleted: next } : item)));
    try {
      const updated = await todosApi.update(todo.externalId, {
        isCompleted: next,
        expectedUpdatedAt: todo.updatedAt,
      });
      setItems((prev) =>
        prev.map((item) =>
          item.externalId === todo.externalId
            ? { ...item, ...updated, description: updated?.description || item.description }
            : item,
        ),
      );
      onActivity?.();
    } catch {
      setItems((prev) => prev.map((item) => (item.externalId === todo.externalId ? todo : item)));
    }
  };

  const saveEdit = async (todo: TodoDto) => {
    const description = editDraft.trim();
    if (!description || saving) return;
    setSaving(true);
    try {
      const updated = await todosApi.update(todo.externalId, {
        description,
        expectedUpdatedAt: todo.updatedAt,
      });
      setItems((prev) =>
        prev.map((item) =>
          item.externalId === todo.externalId
            ? { ...item, ...updated, description: updated?.description || description }
            : item,
        ),
      );
      setEditingId(null);
      onActivity?.();
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    await todosApi.remove(target.externalId);
    setItems((prev) => prev.filter((item) => item.externalId !== target.externalId));
    onActivity?.();
  };

  const myCount = scope === 'counselor' ? items.length : otherCount;
  const studentCount = scope === 'student' ? items.length : otherCount;
  const total = viewerRole === 'counselor' ? myCount + studentCount : items.length;
  const activeCount = items.filter((todo) => !todo.isCompleted).length;
  const completedCount = items.filter((todo) => todo.isCompleted).length;
  const author = viewerFirstName || 'you';
  const shared = viewerRole === 'counselor' && scope === 'student';
  const countLabel =
    completedCount > 0 ? `${activeCount} active, ${completedCount} completed` : `${activeCount} active`;

  const emptyCopy =
    viewerRole === 'student'
      ? "You're all caught up! Add a to-do to keep track of your next steps."
      : shared
        ? `No shared todos yet. Assign one so ${name} knows what to do next.`
        : `No todos yet. Add something you need to remember to do for ${name}.`;

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ className: 'gm-modal' }}>
        <div className="gm-modal-head">
          <div className="gm-modal-head-icon">
            <IconTodo />
          </div>
          <div className="gm-modal-head-text">
            <div className="gm-modal-title">
              {viewerRole === 'counselor' ? `ToDos for ${name}` : 'Your ToDos'}
              <span className="gm-modal-title-count">({total})</span>
            </div>
          </div>
          <button type="button" className="gm-modal-close" aria-label="Close to-dos" onClick={onClose}>
            <IconClose />
          </button>
        </div>
        {viewerRole === 'counselor' && (
          <div className="gm-tab-switcher">
            <button
              type="button"
              className={`gm-tab-switch${scope === 'counselor' ? ' active' : ''}`}
              onClick={() => setScope('counselor')}
            >
              <IconPerson />
              My ToDos
              <span className="gm-tab-count">{myCount}</span>
            </button>
            <button
              type="button"
              className={`gm-tab-switch${scope === 'student' ? ' active' : ''}`}
              onClick={() => setScope('student')}
            >
              <IconCap />
              Student ToDos
              <span className="gm-tab-count">{studentCount}</span>
            </button>
          </div>
        )}
        <div className="gm-modal-body">
          {viewerRole === 'counselor' &&
            (shared ? (
              <div className="gm-privacy-tag shared">
                <IconUsers />
                Visible to {name}
              </div>
            ) : (
              <div className="gm-privacy-tag">
                <IconLock />
                Only you see this
              </div>
            ))}
          <div className="gm-search-bar">
            <span className="gm-search-icon">
              <IconSearch />
            </span>
            <input
              placeholder={
                viewerRole === 'student'
                  ? 'Search your to-dos...'
                  : shared
                    ? `Search ${name}'s todos...`
                    : `Search my todos for ${name}...`
              }
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          {/* HEADER-DOCS-TODOS-V2: students can add personal to-dos (always assigned to themselves). */}
          {(viewerRole === 'counselor' || isStudentViewer) && (
            <div className="gm-todo-add">
              <input
                placeholder={
                  isStudentViewer
                    ? 'Add a to-do for yourself...'
                    : shared
                      ? `Assign a new todo to ${name}...`
                      : 'Add a new todo for yourself...'
                }
                value={draft}
                onChange={(event) => setDraft(event.target.value.slice(0, MAX))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addTodo();
                  }
                }}
              />
              <button type="button" className="gm-plus-btn" aria-label="Add to-do" onClick={addTodo} disabled={!draft.trim() || saving}>
                <IconPlus />
              </button>
            </div>
          )}
          {draft.length >= 400 && <div className="gm-char-count">{draft.length}/{MAX}</div>}
          <div className="gm-list-label">
            <span className="gm-list-label-title">
              {viewerRole === 'student' ? 'Your ToDo list' : shared ? `${name}'s ToDo list` : 'My ToDo list'}
            </span>
            <span className="gm-list-label-count">{countLabel}</span>
          </div>
          {loading ? (
            <div className="gm-modal-empty">Loading to-dos…</div>
          ) : visible.length === 0 ? (
            <div className="gm-modal-empty">{filter ? 'No to-dos match that search.' : emptyCopy}</div>
          ) : (
            visible.map((todo) => {
              const studentAuthored = isStudentAuthored(todo);
              // HEADER-DOCS-TODOS-V2: meta line distinguishes student-added from counselor-assigned to-dos.
              let meta: string;
              if (todo.isCompleted && todo.completedAt) {
                meta = `Completed ${formatModalDate(todo.completedAt)}`;
              } else if (isStudentViewer) {
                meta = studentAuthored
                  ? `Added ${formatModalDate(todo.createdAt)}`
                  : `Assigned by your counselor · ${formatModalDate(todo.createdAt)}`;
              } else if (shared) {
                meta = studentAuthored
                  ? `Added by ${name} · ${formatModalDate(todo.createdAt)}`
                  : `Assigned by ${author} · ${formatModalDate(todo.createdAt)}`;
              } else {
                meta = `Added ${formatModalDate(todo.createdAt)}`;
              }
              return (
                <div key={todo.externalId} className="gm-item-card">
                  {editingId === todo.externalId ? (
                    <div className="gm-item-body gm-item-edit">
                      <div className="gm-todo-add">
                        <input
                          aria-label="Edit to-do"
                          value={editDraft}
                          autoFocus
                          onChange={(event) => setEditDraft(event.target.value.slice(0, MAX))}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              saveEdit(todo);
                            } else if (event.key === 'Escape') {
                              event.stopPropagation();
                              setEditingId(null);
                            }
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button
                          type="button"
                          className="gm-btn-primary"
                          onClick={() => saveEdit(todo)}
                          disabled={!editDraft.trim() || saving}
                        >
                          Save
                        </button>
                        <button type="button" className="gm-btn-secondary" onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={`gm-check${todo.isCompleted ? ' done' : ''}`}
                        aria-label={todo.isCompleted ? `Mark "${todo.description}" not complete` : `Mark "${todo.description}" complete`}
                        onClick={() => toggle(todo)}
                      >
                        {todo.isCompleted && <IconCheck />}
                      </button>
                      <div className="gm-item-body">
                        <div className={`gm-item-title${todo.isCompleted ? ' done' : ''}`}>{todo.description}</div>
                        <div className="gm-item-meta">
                          {shared && (
                            <span className={`gm-author-badge${studentAuthored ? ' student' : ''}`}>
                              {studentAuthored ? 'Student' : 'Counselor'}
                            </span>
                          )}
                          {meta}
                        </div>
                      </div>
                      {(canEditTodo(todo) || canDeleteTodo(todo)) && (
                        <div className="gm-item-actions">
                          {canEditTodo(todo) && (
                            <button
                              type="button"
                              className="gm-icon-btn"
                              aria-label="Edit to-do"
                              onClick={() => {
                                setEditingId(todo.externalId);
                                setEditDraft(todo.description);
                              }}
                            >
                              <IconPencil />
                            </button>
                          )}
                          {canDeleteTodo(todo) && (
                            <button type="button" className="gm-icon-btn" aria-label="Delete to-do" onClick={() => setPendingDelete(todo)}>
                              <IconTrash />
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Dialog>
      <GmDeleteDialog
        open={Boolean(pendingDelete)}
        title="Delete this to-do?"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default TodosModal;
